/**
 * App-native confirm and prompt — the replacement for `window.confirm` and
 * `window.prompt`.
 *
 * Kai, 2026-09-08: *"I want to remove all of these localhost:3001 says …
 * alerts, I want everything to be native."* Native to the app, not to the
 * browser. The browser's dialogs are wrong here for four separate reasons and
 * any one of them would do:
 *
 *  - they announce the ORIGIN ("localhost:3001 says"), which on a white-label
 *    product means a customer's portal shows our hostname;
 *  - they cannot be styled, so the most consequential moment in a flow is the
 *    only one that looks like nothing else in the product;
 *  - they block the whole page synchronously, including anything mid-flight;
 *  - `prompt` in particular is unstyleable, unvalidatable and unlabellable.
 *
 * Promise-based so a call site reads almost exactly as it did before:
 *
 *     if (!(await confirm({ title: 'Delete “X”?' }))) return;
 *
 * which is what made converting twenty-odd sites safe to do in one pass.
 *
 * Separate from `DirtyProvider`'s unsaved-changes question on purpose: that
 * one is asked BY the router about state the router owns, and it has exactly
 * one wording. This is asked by a page about its own action.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export interface ConfirmOptions {
  title: string;
  /** The consequence, when it is not obvious from the title. */
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Paints the confirm button red. For anything that destroys or revokes. */
  destructive?: boolean;
}

export interface PromptOptions {
  title: string;
  label?: string;
  placeholder?: string;
  initial?: string;
  confirmLabel?: string;
}

type Pending =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void };

const Ctx = createContext<{
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}>({
  // Defaults that REFUSE rather than proceed. A destructive action firing
  // because a provider was missing is the one failure mode worth designing
  // against; a no-op is recoverable, a delete is not.
  confirm: async () => false,
  prompt: async () => null,
});

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [text, setText] = useState('');

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ kind: 'confirm', opts, resolve })),
    [],
  );
  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setText(opts.initial ?? '');
        setPending({ kind: 'prompt', opts, resolve });
      }),
    [],
  );

  const close = (value: boolean | string | null) => {
    if (!pending) return;
    if (pending.kind === 'confirm') pending.resolve(value === true);
    else pending.resolve(typeof value === 'string' ? value : null);
    setPending(null);
  };

  return (
    <Ctx.Provider value={{ confirm, prompt }}>
      {children}
      {pending && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          onClick={() => close(pending.kind === 'confirm' ? false : null)}
        >
          <div
            role="alertdialog"
            aria-label={pending.opts.title}
            className="w-full max-w-sm rounded-lg border bg-card p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold">{pending.opts.title}</h2>
            {pending.kind === 'confirm' && pending.opts.body && (
              <p className="mt-1 text-sm text-muted-foreground">{pending.opts.body}</p>
            )}
            {pending.kind === 'prompt' && (
              <form
                className="mt-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  close(text);
                }}
              >
                {pending.opts.label && (
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    {pending.opts.label}
                  </label>
                )}
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={pending.opts.placeholder ?? ''}
                  autoFocus
                />
              </form>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => close(pending.kind === 'confirm' ? false : null)}
              >
                {(pending.kind === 'confirm' && pending.opts.cancelLabel) || 'Cancel'}
              </Button>
              <Button
                variant={pending.kind === 'confirm' && pending.opts.destructive ? 'destructive' : 'default'}
                size="sm"
                onClick={() => close(pending.kind === 'confirm' ? true : text)}
              >
                {pending.opts.confirmLabel ?? (pending.kind === 'prompt' ? 'Save' : 'Confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

/** `await confirm({ title })` → true if they went ahead. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  return useContext(Ctx).confirm;
}

/** `await prompt({ title })` → the text, or null if they cancelled. */
export function usePrompt(): (opts: PromptOptions) => Promise<string | null> {
  return useContext(Ctx).prompt;
}
